import { Feature } from 'ol';
import { Coordinate } from 'ol/coordinate';
import { Field } from 'src/app/modelli/field.model';
import { Collection, ConfigApiInterface } from './model/config-api-response.interface';
import { RelatedItemInterface, RelatedItemsInterface } from './model/related-item.interface';

/**
 * Extracts the feature collection name from its urn.
 *
 * Example urn: G4BrefVrecordVdatasetZINWITVcollectionZBTSVkeysZI056PS
 * Extracted name: BTS
 * @param {string} urn The urn from which to extract the collection name.
 * @returns The feature collection name.
 */
export function getCollectionName(urn: string) {
  return urn.split('V')[3].split('Z')[1];
}

/**
 * Extracts the feature collection dataset name from its urn.
 *
 * Example urn: G4BrefVrecordVdatasetZINWITVcollectionZBTSVkeysZI056PS
 * Extracted name: INWIT
 * @param {string} urn The urn from which to extract the collection name.
 * @returns The feature collection name.
 */
export function getCollectionDataset(urn: string) {
  return urn.split('V')[2].split('Z')[1];
}

// todo : make type for data
export function extractRelatedItems(data, configApi: ConfigApiInterface): RelatedItemsInterface {
  const collectionName = getCollectionName(data.features[0].id);
  const configOfCollection = configApi.data.collections.filter((c) => c.name.includes(collectionName))[0];
  const relatedItemsOfStrings = [];
  let relatedItems: RelatedItemsInterface = [];
  const dataCopy = JSON.parse(JSON.stringify(data));
  if (configOfCollection) {
    configOfCollection.fields.forEach((f) => {
      if (f.join && !f.is_container && dataCopy.features[0].properties[f.name]) {
        relatedItemsOfStrings.push(...dataCopy.features[0].properties[f.name]);
      }
    });
    relatedItems = parseArrayOfUrnStrings(relatedItemsOfStrings, configApi);
    dataCopy.features[0]['relatedItems'] = relatedItems.map((o) => ({ ...o }));
  }
  // console.log({dataCopy, configApi, collectionName, configOfCollection})
  return relatedItems;
}

export function parseArrayOfUrnStrings(
  relatedObjects: string[],
  configApi: ConfigApiInterface
): RelatedItemsInterface {
  return relatedObjects
    .map((s) => JSON.parse(s))
    .map((o) => ({ ...o, name: getCollectionName(o.urn) }))
    .map((o) => ({
      ...o,
      alias: configApi.data.collections.filter((f) => f.name.includes(o.name))[0].external_name,
      clickable: configApi.data.collections
        .filter((f) => f.name.includes(o.name))[0]
        .actions.includes('view'),
    }));
}

export function createRelativeItemFromUrnAndConfig(
  urn: string,
  apiConfig: ConfigApiInterface
): RelatedItemInterface | undefined {
  const urnCollection = getCollectionName(urn);
  const configOfUrnCollection = apiConfig.data.collections.find((c) => c.name.includes(urnCollection));
  return configOfUrnCollection
    ? {
        urn: urn,
        name: configOfUrnCollection.name,
        label: '',
        alias: configOfUrnCollection.external_name,
        clickable: !!configOfUrnCollection.actions['view'],
      }
    : undefined;
}

export function createRelativeItemFromFeatureAndConfig(
  feature,
  apiConfig: ConfigApiInterface
): RelatedItemInterface | undefined {
  const urn = feature.id;
  const featureProps =  feature.properties;
  const urnCollection = getCollectionName(urn);
  const configOfUrnCollection = apiConfig.data.collections.find((c) => c.name.includes(urnCollection));
  return configOfUrnCollection
    ? {
        urn: urn,
        name: configOfUrnCollection.name,
        label: `${featureProps.description}, ${featureProps.spec_id}, ${featureProps.construction_status}`,
        alias: configOfUrnCollection.external_name,
        clickable: true,
      }
    : undefined;
}

// todo : which relatedItem is clickable ?

export function staticImagePixelFromCoordinates(
  extent: number[],
  imageSize: number[],
  coordinates: Coordinate
): Coordinate {
  //Extent: [minx, miny, maxx, maxy]
  //imageSize: [width, height]
  let pixelSizeX = (extent[2] - extent[0]) / imageSize[0];
  let pixelSizeY = (extent[3] - extent[1]) / imageSize[1];

  let pixelX = Math.floor((coordinates[0] - extent[0]) / pixelSizeX);
  let pixelY = Math.floor((coordinates[1] - extent[1]) / pixelSizeY);
  return [pixelX, pixelY];
}

function clamp(input: number, min: number, max: number): number {
  return input < min ? min : input > max ? max : input;
}

export function mapNumbers(current: number, in_min: number, in_max: number, out_min: number, out_max: number): number {
  const mapped: number = ((current - in_min) * (out_max - out_min)) / (in_max - in_min) + out_min;
  return clamp(mapped, out_min, out_max);
}